/**
 * Validation schemas shared by the Worker and the web app.
 *
 * The Worker validates every request body with these before touching the
 * database — that is the enforcing check. The web app uses the same schemas to
 * give immediate feedback in forms, so the two can never disagree about what a
 * valid payload looks like.
 */

import { z } from "zod";
import { BASIS_POINTS_DENOMINATOR } from "./money.js";
import { PERMISSION_KEYS } from "./permissions.js";

/* ── Primitives ────────────────────────────────────────────────────────── */

const trimmed = (max: number) => z.string().trim().max(max);

/** #RGB or #RRGGBB. Rejected values would silently break CSS custom properties. */
export const hexColorSchema = z
  .string()
  .trim()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "must be a hex colour such as #1D4ED8");

export const usernameSchema = z
  .string()
  .trim()
  .min(3, "must be at least 3 characters")
  .max(64, "must be at most 64 characters")
  .regex(/^[a-zA-Z0-9._-]+$/, "may contain only letters, digits, dot, underscore and hyphen");

/**
 * Password policy.
 *
 * Length does more for resistance to guessing than character-class rules do, so
 * the floor is 10 with a single composition requirement to rule out the
 * degenerate all-digit PIN. The upper bound exists because PBKDF2 hashing cost
 * scales with input length and an unbounded field is a cheap denial-of-service.
 */
export const passwordSchema = z
  .string()
  .min(10, "must be at least 10 characters")
  .max(256, "must be at most 256 characters")
  .refine((v) => /[a-zA-Z]/.test(v) && /[0-9]/.test(v), {
    message: "must contain at least one letter and one number",
  });

export const permissionKeySchema = z.enum(
  PERMISSION_KEYS as unknown as [string, ...string[]],
);

/** Saudi VAT registration numbers are 15 digits. Optional until the owner has one. */
export const vatNumberSchema = z
  .string()
  .trim()
  .regex(/^\d{15}$/, "must be exactly 15 digits")
  .nullable()
  .optional();

const optionalText = (max: number) =>
  trimmed(max)
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v));

/* ── Authentication ────────────────────────────────────────────────────── */

export const loginSchema = z.object({
  username: z.string().trim().min(1).max(64),
  password: z.string().min(1).max(256),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * First-run setup. Creates the one and only owner account, and is permanently
 * disabled once the installation is initialised.
 */
export const setupSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
  fullName: trimmed(120).min(1, "is required"),
  email: z.string().trim().email().max(200).nullable().optional(),
  businessNameAr: trimmed(200).min(1, "is required"),
  businessNameEn: trimmed(200).optional().default(""),
  /** Required only when the deployment sets a SETUP_TOKEN secret. */
  setupToken: z.string().max(200).optional(),
});
export type SetupInput = z.infer<typeof setupSchema>;

/* ── Users ─────────────────────────────────────────────────────────────── */

export const createUserSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
  fullName: trimmed(120).min(1, "is required"),
  email: z.string().trim().email().max(200).nullable().optional(),
  phone: optionalText(40),
  /**
   * Role IDs to assign. The Worker independently rejects any owner role here —
   * the owner role is never assignable through ordinary user management, and
   * that rule is enforced server-side rather than by omitting it from a form.
   */
  roleIds: z.array(z.string().min(1)).max(20).default([]),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  fullName: trimmed(120).min(1).optional(),
  email: z.string().trim().email().max(200).nullable().optional(),
  phone: optionalText(40),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const assignRolesSchema = z.object({
  roleIds: z.array(z.string().min(1)).max(20),
});
export type AssignRolesInput = z.infer<typeof assignRolesSchema>;

export const setUserActiveSchema = z.object({
  active: z.boolean(),
});

export const resetPasswordSchema = z.object({
  newPassword: passwordSchema,
});

/* ── Roles ─────────────────────────────────────────────────────────────── */

export const roleKeySchema = z
  .string()
  .trim()
  .min(2)
  .max(48)
  .regex(/^[a-z][a-z0-9_]*$/, "may contain only lowercase letters, digits and underscore");

export const createRoleSchema = z.object({
  key: roleKeySchema,
  nameAr: trimmed(80).min(1, "is required"),
  nameEn: trimmed(80).min(1, "is required"),
  descriptionAr: optionalText(400),
  descriptionEn: optionalText(400),
  permissions: z.array(permissionKeySchema).max(PERMISSION_KEYS.length).default([]),
});
export type CreateRoleInput = z.infer<typeof createRoleSchema>;

export const updateRoleSchema = z.object({
  nameAr: trimmed(80).min(1).optional(),
  nameEn: trimmed(80).min(1).optional(),
  descriptionAr: optionalText(400),
  descriptionEn: optionalText(400),
  permissions: z.array(permissionKeySchema).max(PERMISSION_KEYS.length).optional(),
});
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;

/* ── Business settings ─────────────────────────────────────────────────── */

export const businessSettingsSchema = z.object({
  legalNameAr: trimmed(200).min(1, "is required"),
  legalNameEn: trimmed(200).optional().default(""),
  tradeName: optionalText(200),
  vatNumber: vatNumberSchema,
  crNumber: optionalText(40),
  phone: optionalText(40),
  email: z.string().trim().email().max(200).nullable().optional(),
  website: optionalText(200),
  buildingNumber: optionalText(20),
  street: optionalText(200),
  district: optionalText(120),
  city: optionalText(120),
  postalCode: optionalText(20),
  additionalNumber: optionalText(20),
  country: trimmed(2).min(2).max(2).default("SA"),
  currency: trimmed(3).min(3).max(3).default("SAR"),
  language: z.enum(["ar", "en"]).default("ar"),
  timezone: trimmed(64).min(1).default("Asia/Riyadh"),
});
export type BusinessSettingsInput = z.infer<typeof businessSettingsSchema>;

/* ── Branding ──────────────────────────────────────────────────────────── */

export const brandingSettingsSchema = z.object({
  appNameAr: trimmed(80).min(1, "is required"),
  appNameEn: trimmed(80).min(1, "is required"),
  primaryColor: hexColorSchema,
  secondaryColor: hexColorSchema,
  loginHeadlineAr: optionalText(200),
  loginHeadlineEn: optionalText(200),
  footerTextAr: optionalText(200),
  footerTextEn: optionalText(200),
});
export type BrandingSettingsInput = z.infer<typeof brandingSettingsSchema>;

/* ── Tax ───────────────────────────────────────────────────────────────── */

/**
 * Updating tax settings INSERTS a new historised row rather than mutating the
 * current one, so an invoice issued last month can still resolve the rate that
 * was in force when it was issued.
 */
export const taxSettingsSchema = z.object({
  vatEnabled: z.boolean(),
  rateBps: z
    .number()
    .int("must be a whole number of basis points")
    .min(0)
    .max(BASIS_POINTS_DENOMINATOR, `must not exceed ${BASIS_POINTS_DENOMINATOR} basis points (100%)`),
  pricesInclusive: z.boolean(),
});
export type TaxSettingsInput = z.infer<typeof taxSettingsSchema>;

/* ── Invoice settings ──────────────────────────────────────────────────── */

export const invoiceSettingsSchema = z.object({
  invoicePrefix: z
    .string()
    .trim()
    .max(12)
    .regex(/^[A-Z0-9-]*$/, "may contain only uppercase letters, digits and hyphen")
    .default("INV-"),
  numberPadding: z.number().int().min(1).max(12).default(6),
  footerTextAr: optionalText(300),
  footerTextEn: optionalText(300),
  defaultFormat: z.enum(["a4_pdf", "thermal_80", "thermal_58"]).default("thermal_80"),
  thermalWidthMm: z.union([z.literal(58), z.literal(80)]).default(80),
  showQr: z.boolean().default(true),
  showLogo: z.boolean().default(true),
  receiptLanguage: z.enum(["ar", "en", "both"]).default("ar"),

  /* ── Presentation only ──────────────────────────────────────────────────
     Everything below decides whether and how an ALREADY-FROZEN value is
     displayed. None of it can change a number: an issued invoice's money
     lives in snapshot columns written at issuance, so a reprint after a
     settings change shows the same totals it always did. See migration
     0011. */

  defaultCopies: z.number().int().min(1).max(5).default(1),
  /** Fires the print dialog straight after a committed Direct Sale. */
  autoPrintDirectSale: z.boolean().default(false),
  printPreview: z.boolean().default(true),

  logoSize: z.enum(["small", "medium", "large"]).default("medium"),
  logoAlign: z.enum(["start", "center", "end"]).default("center"),

  showBusinessName: z.boolean().default(true),
  showVatNumber: z.boolean().default(true),
  showAddress: z.boolean().default(true),
  showDatetime: z.boolean().default(true),
  showCustomer: z.boolean().default(true),
  showPaymentMethod: z.boolean().default(true),
  showTaxDetail: z.boolean().default(true),
  showNotes: z.boolean().default(true),

  qrSize: z.enum(["small", "medium", "large"]).default("medium"),
  qrPosition: z.enum(["top", "bottom"]).default("bottom"),
});
export type InvoiceSettingsInput = z.infer<typeof invoiceSettingsSchema>;

/* ── Query helpers ─────────────────────────────────────────────────────── */

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const auditQuerySchema = paginationSchema.extend({
  action: z.string().trim().max(80).optional(),
  entity: z.string().trim().max(80).optional(),
  actorUserId: z.string().trim().max(64).optional(),
});

/* ══════════════════════════════════════════════════════════════════════════
   Phase 2 — laundry domain
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Saudi mobile numbers, stored normalised.
 *
 * Accepts what people actually type — 05xxxxxxxx, +9665xxxxxxxx, 9665xxxxxxxx,
 * with or without spaces or dashes — and normalises to 05xxxxxxxx so the unique
 * index treats them as the same customer.
 */
export const mobileSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-()]/g, ""))
  .refine((v) => /^(?:\+?966|0)?5\d{8}$/.test(v), { message: "must be a valid Saudi mobile number" })
  .transform((v) => `0${v.replace(/^(?:\+?966|0)/, "")}`);

export const createCustomerSchema = z.object({
  name: trimmed(160).min(1, "is required"),
  mobile: mobileSchema.nullable().optional(),
  email: z.string().trim().email().max(200).nullable().optional(),
  address: optionalText(400),
  notes: optionalText(1000),
});
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;

export const updateCustomerSchema = createCustomerSchema.partial();
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

export const customerQuerySchema = paginationSchema.extend({
  /** Matches name, mobile, or an order number/reference. */
  search: z.string().trim().max(120).optional(),
  includeDeleted: z.coerce.boolean().optional().default(false),
});

/* ── Catalog ─────────────────────────────────────────────────────────────── */

const bilingualNameSchema = {
  nameAr: trimmed(120).min(1, "is required"),
  nameEn: trimmed(120).min(1, "is required"),
  sortOrder: z.number().int().min(0).max(9999).optional().default(0),
};

export const createItemTypeSchema = z.object({ ...bilingualNameSchema });
export type CreateItemTypeInput = z.infer<typeof createItemTypeSchema>;

export const updateItemTypeSchema = z.object({
  nameAr: trimmed(120).min(1).optional(),
  nameEn: trimmed(120).min(1).optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});

export const createServiceCategorySchema = z.object({ ...bilingualNameSchema });
export const updateServiceCategorySchema = updateItemTypeSchema;

export const createServiceSchema = z.object({
  ...bilingualNameSchema,
  categoryId: z.string().min(1).nullable().optional(),
  description: optionalText(600),
  pricingMode: z.enum(["per_item", "per_kg", "flat"]).default("per_item"),
});
export type CreateServiceInput = z.infer<typeof createServiceSchema>;

export const updateServiceSchema = z.object({
  nameAr: trimmed(120).min(1).optional(),
  nameEn: trimmed(120).min(1).optional(),
  categoryId: z.string().min(1).nullable().optional(),
  description: optionalText(600),
  pricingMode: z.enum(["per_item", "per_kg", "flat"]).optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});

/* ── Pricing ─────────────────────────────────────────────────────────────── */

/**
 * Prices cross the wire as INTEGER MINOR UNITS, never as a decimal.
 *
 * A decimal would arrive as a float and lose precision before any validation
 * could defend against it — see packages/shared/src/money.ts.
 */
export const setPriceSchema = z.object({
  serviceId: z.string().min(1),
  /** null sets the service's fallback price, used when no pair-specific one exists. */
  itemTypeId: z.string().min(1).nullable(),
  unitPriceMinor: z
    .number()
    .int("must be a whole number of minor units")
    .min(0, "must not be negative")
    .max(10_000_000_000_000),
});
export type SetPriceInput = z.infer<typeof setPriceSchema>;

/* ── Orders ──────────────────────────────────────────────────────────────── */

export const orderItemInputSchema = z.object({
  itemTypeId: z.string().min(1),
  serviceId: z.string().min(1),
  quantity: z.number().positive("must be greater than zero").max(100_000),
  /** Absolute discount on this line, in minor units. Applied before tax. */
  discountMinor: z.number().int().min(0).max(10_000_000_000_000).optional().default(0),
  notes: optionalText(400),
});
export type OrderItemInput = z.infer<typeof orderItemInputSchema>;

export const createOrderSchema = z.object({
  /**
   * OPTIONAL. A laundry counter takes anonymous walk-ins, and forcing a
   * customer here would make the operator either interrogate someone dropping
   * off two shirts or invent a placeholder record. `null`/absent means
   * genuinely anonymous — see migration 0009 for why the column is nullable
   * rather than pointing at a synthetic "Walk-in" customer row.
   */
  customerId: z.string().min(1).nullable().optional(),
  items: z.array(orderItemInputSchema).min(1, "an order needs at least one item"),
  expectedReadyAt: z.string().datetime().nullable().optional(),
  notes: optionalText(1000),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

/**
 * Direct Sale — a completed counter transaction, paid in full.
 *
 * NOTE WHAT IS ABSENT: there is no payment amount. The operator never types
 * one. The server prices the lines, computes VAT, and posts a payment for
 * exactly the total it calculated — so a tampered or stale client amount has
 * nothing to influence.
 *
 * `expectedTotalMinor` is the one number a client may send, and it is only
 * ever COMPARED, never used. It exists so a POS showing a stale price (the
 * owner repriced mid-sale) is refused rather than silently charging a
 * different amount than the operator quoted.
 */
export const directSaleSchema = z.object({
  customerId: z.string().min(1).nullable().optional(),
  items: z.array(orderItemInputSchema).min(1, "a sale needs at least one item"),
  method: z.enum(["CASH", "CARD", "TRANSFER", "OTHER"]),
  notes: optionalText(1000),
  /** Optional client-side assertion of the total. Compared, never trusted. */
  expectedTotalMinor: z.number().int().min(0).optional(),
});
export type DirectSaleInput = z.infer<typeof directSaleSchema>;

export const updateOrderSchema = z.object({
  expectedReadyAt: z.string().datetime().nullable().optional(),
  notes: optionalText(1000),
  items: z.array(orderItemInputSchema).min(1).optional(),
});
export type UpdateOrderInput = z.infer<typeof updateOrderSchema>;

export const orderStatusChangeSchema = z.object({
  status: z.enum(["RECEIVED", "INSPECTION", "PROCESSING", "READY", "DELIVERED", "CANCELLED"]),
  reason: optionalText(400),
});
export type OrderStatusChangeInput = z.infer<typeof orderStatusChangeSchema>;

export const orderQuerySchema = paginationSchema.extend({
  /** Matches order number/reference, customer name, or customer mobile. */
  search: z.string().trim().max(120).optional(),
  status: z
    .enum(["RECEIVED", "INSPECTION", "PROCESSING", "READY", "DELIVERED", "CANCELLED"])
    .optional(),
  customerId: z.string().trim().max(64).optional(),
});
